import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { BrineFormulationService } from 'src/app/services/brine-formulation.service';
import {
  BrineCalculationResultDto,
  BrineTypeDto,
  SaveBrineFormulationDto,
} from '@models/brine-formulation-dto.interface';

type DriverField = 'brineDensityPpg' | 'brineWtPercent' | 'brineWpsPpm' | 'brineWpsMgPerL';
const DRIVER_FIELDS: DriverField[] = ['brineDensityPpg', 'brineWtPercent', 'brineWpsPpm', 'brineWpsMgPerL'];

@Component({
  selector: 'app-brine-formulation',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule],
  templateUrl: './brine-formulation.component.html',
  styleUrl: './brine-formulation.component.scss',
})
export class BrineFormulationComponent implements OnInit, OnChanges {
  @Input() jobRequestId: number | null = null;
  @Input() isEditable = false;
  @Input() externalBrineVolumeMl: number | null = null;
  @Output() changed = new EventEmitter<void>();
  @Output() densityResolved = new EventEmitter<number | null>();

  private readonly brineFormulationService = inject(BrineFormulationService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected brineTypes = signal<BrineTypeDto[]>([]);
  protected result = signal<BrineCalculationResultDto | null>(null);
  protected isLoading = signal(false);
  protected isCalculatingSignal = signal(false);
  protected isSaving = signal(false);
  protected errorMessage = signal('');
  protected isCalculating() { return this.isCalculatingSignal(); }

  private driverField: DriverField | null = null;

  protected form = this.fb.group({
    brineTypeId: [null as number | null, Validators.required],
    brineDensityPpg: [null as number | null],
    brineWtPercent: [null as number | null],
    brineWpsPpm: [null as number | null],
    brineWpsMgPerL: [null as number | null],
    brineVolumeMl: [null as number | null, Validators.min(0)],
  });

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select[formControlName]:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  ngOnInit(): void {
    this.brineFormulationService.getBrineTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(types => {
        this.brineTypes.set(types);
        if (this.form.controls.brineTypeId.value === null && types.length > 0) {
          this.form.controls.brineTypeId.setValue(types[0].id);
        }
      });

    this.form.get('brineTypeId')!.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.recalculate());

    for (const field of DRIVER_FIELDS) {
      this.form.get(field)!.valueChanges
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(value => {
          this.driverField = value !== null ? field : this.driverField;
          this.recalculate();
        });
    }

    this.form.get('brineVolumeMl')!.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.recalculate());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jobRequestId'] && this.jobRequestId) {
      this.loadExisting(this.jobRequestId);
    }    

    // Mud Formulation computes Brine (ml) and pushes it back here to recalculate "For Specified Volume".
    if (changes['externalBrineVolumeMl'] && !changes['externalBrineVolumeMl'].isFirstChange()) {
      const value = this.externalBrineVolumeMl;
      if (value !== null && value !== this.form.get('brineVolumeMl')!.value) {
        this.form.get('brineVolumeMl')!.setValue(value);
      }
    }
  }

  protected onSave(): void {
    if (!this.jobRequestId || !this.driverField || this.isSaving()) return;

    const raw = this.form.getRawValue();
    const dto: SaveBrineFormulationDto = {
      brineTypeId: raw.brineTypeId!,
      brineDensityPpg: this.driverField === 'brineDensityPpg' ? raw.brineDensityPpg : null,
      brineWtPercent: this.driverField === 'brineWtPercent' ? raw.brineWtPercent : null,
      brineWpsPpm: this.driverField === 'brineWpsPpm' ? raw.brineWpsPpm : null,
      brineWpsMgPerL: this.driverField === 'brineWpsMgPerL' ? raw.brineWpsMgPerL : null,
      brineVolumeMl: raw.brineVolumeMl,
    };

    this.isSaving.set(true);
    this.errorMessage.set('');

    this.brineFormulationService.save(this.jobRequestId, dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: saved => {
          this.isSaving.set(false);
          this.result.set(saved.calculation);
          this.densityResolved.emit(saved.calculation.densityPpg);
          this.changed.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.isSaving.set(false);
          this.errorMessage.set(this.extractErrorMessage(err));
        },
      });
  }

  private loadExisting(jobRequestId: number): void {
    this.isLoading.set(true);
    this.brineFormulationService.getByJobRequest(jobRequestId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dto => {
          this.isLoading.set(false);
          this.result.set(dto.calculation);
          this.densityResolved.emit(dto.calculation.densityPpg);
          // All four fields are stored fully-resolved and mutually consistent; default further edits to density.
          this.driverField = 'brineDensityPpg';
          this.form.patchValue({
            brineTypeId: dto.calculation.brineTypeId,
            brineDensityPpg: dto.calculation.densityPpg,
            brineWtPercent: dto.calculation.wtPercent,
            brineWpsPpm: dto.calculation.wpsPpm,
            brineWpsMgPerL: dto.calculation.wpsMgPerL,
            brineVolumeMl: dto.calculation.brineVolumeMl,
          }, { emitEvent: false });
        },
        error: () => {
          // No saved formulation yet for this job request.
          this.isLoading.set(false);
        },
      });
  }

  private recalculate(): void {
    if (!this.driverField) return;

    const raw = this.form.getRawValue();
    const driverValue = raw[this.driverField];
    if (driverValue === null || driverValue === undefined) return;

    this.isCalculatingSignal.set(true);
    this.errorMessage.set('');

    this.brineFormulationService.calculate({
      brineTypeId: raw.brineTypeId!,
      brineDensityPpg: this.driverField === 'brineDensityPpg' ? raw.brineDensityPpg : null,
      brineWtPercent: this.driverField === 'brineWtPercent' ? raw.brineWtPercent : null,
      brineWpsPpm: this.driverField === 'brineWpsPpm' ? raw.brineWpsPpm : null,
      brineWpsMgPerL: this.driverField === 'brineWpsMgPerL' ? raw.brineWpsMgPerL : null,
      brineVolumeMl: raw.brineVolumeMl,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: calc => {
        this.isCalculatingSignal.set(false);
        this.result.set(calc);
        this.densityResolved.emit(calc.densityPpg);
        this.form.patchValue({
          brineDensityPpg: calc.densityPpg,
          brineWtPercent: calc.wtPercent,
          brineWpsPpm: calc.wpsPpm,
          brineWpsMgPerL: calc.wpsMgPerL,
        }, { emitEvent: false });
      },
      error: (err: HttpErrorResponse) => {
        this.isCalculatingSignal.set(false);
        this.result.set(null);
        this.errorMessage.set(this.extractErrorMessage(err));
      },
    });
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const errors = err.error?.errors as Record<string, string[]> | undefined;
    if (errors) {
      const firstMessage = Object.values(errors)[0]?.[0];
      if (firstMessage) return firstMessage;
    }
    return 'Unable to calculate brine formulation. Please check your entries.';
  }
}
